import { Graphics, Sprite, Texture, Assets } from 'pixi.js';
import { DropData } from '../systems/NetworkManager';
import { DROP_CONFIG, type SelectionInfo, type EntityType } from '@astra/shared';
import { tween } from '../core/TweenManager';
import { MovableEntity } from './base/MovableEntity';

// SVG texture cache
let dropSvgTexture: Texture | null = null;
let dropSvgPromise: Promise<Texture | null> | null = null;

const DROP_SVG_URL = '/assets/sprites/drop.svg';
const DROP_SCALE = 0.12; // 512px SVG → ~60px in-game

function loadDropSvgTexture(): Promise<Texture | null> {
  if (dropSvgTexture) return Promise.resolve(dropSvgTexture);
  if (!dropSvgPromise) {
    dropSvgPromise = Assets.load<Texture>(DROP_SVG_URL)
      .then((tex) => {
        dropSvgTexture = tex;
        return tex;
      })
      .catch(() => {
        // Allow a future retry on next drop creation
        dropSvgPromise = null;
        return null;
      });
  }
  return dropSvgPromise;
}

// Color tints per drop type
const DROP_TINT_CREDITS = 0xffcc44;   // Gold
const DROP_TINT_EQUIPMENT = 0x44aaff; // Blue

export class Drop extends MovableEntity {
  // Required abstract properties from SpaceEntity
  readonly id: string;
  readonly entityType: EntityType = 'drop';

  // Required abstract properties from TargetableEntity
  readonly selectionRadius: number = 30;
  readonly isHostile: boolean = false;

  // Drop-specific properties (mirror DropData)
  public itemEntries: import('../systems/NetworkManager').DropItemEntry[];
  public ownerSessionId: string;
  public ownerUntil: number;
  public createdAt: number;
  public expiresAt: number;

  private svgSprite: Sprite | null = null;
  private bobOffset: number = 0;
  private bobSpeed: number = 2; // Radians per second
  private bobAmount: number = 3; // Pixels
  private rotationSpeed: number = 0.3; // Radians per second (slow rotation)
  private baseY: number;
  private tintColor: number;

  constructor(data: DropData) {
    super();

    this.position.set(data.x, data.y);

    // Initialize target position for MovableEntity
    this.targetX = data.x;
    this.targetY = data.y;

    this.id = data.id;
    this.itemEntries = data.items;
    this.ownerSessionId = data.ownerSessionId;
    this.ownerUntil = data.ownerUntil;
    this.createdAt = data.createdAt;
    this.expiresAt = data.expiresAt;
    this.baseY = data.y;

    // Determine color based on contents
    const hasEquipment = this.itemEntries.some((it) => it.itemType === 'equipment');
    this.tintColor = hasEquipment ? DROP_TINT_EQUIPMENT : DROP_TINT_CREDITS;

    // Load SVG texture asynchronously
    if (dropSvgTexture) {
      this.createSvgSprite(dropSvgTexture);
    } else {
      loadDropSvgTexture().then((tex) => {
        if (tex && !this.destroyed) {
          this.createSvgSprite(tex);
        }
      });
    }

    // Set depth (above background, below ships)
    this.zIndex = 5;
  }

  private createSvgSprite(texture: Texture): void {
    this.svgSprite = new Sprite(texture);
    this.svgSprite.anchor.set(0.5);
    this.svgSprite.scale.set(DROP_SCALE);
    this.svgSprite.tint = this.tintColor;
    this.addChild(this.svgSprite);
  }

  // Required abstract method from TargetableEntity
  getSelectionInfo(): SelectionInfo {
    const hasEquipment = this.itemEntries.some((it) => it.itemType === 'equipment');
    return {
      type: 'drop',
      id: this.id,
      name: hasEquipment ? 'Loot Crate' : 'Credit Crate',
      x: this.x,
      y: this.baseY,
      data: {
        items: this.itemEntries,
        ownerSessionId: this.ownerSessionId,
        ownerUntil: this.ownerUntil,
        expiresAt: this.expiresAt,
      },
    };
  }

  /** Update inventory from server (drop contents changed). */
  updateInventory(items: import('../systems/NetworkManager').DropItemEntry[]): void {
    this.itemEntries = items;
  }

  update(delta: number): void {
    const dt = delta / 1000;

    // Use inherited updatePosition for smooth interpolation (if drop moves)
    this.updatePosition(delta);

    // Bobbing animation
    this.bobOffset += this.bobSpeed * dt;
    this.y = this.baseY + Math.sin(this.bobOffset) * this.bobAmount;

    // Slow rotation
    if (this.svgSprite) {
      this.svgSprite.rotation += this.rotationSpeed * dt;
    }

    // Check if drop is expiring soon (last 30 seconds) - flash faster
    const now = Date.now();
    const timeLeft = this.expiresAt - now;
    if (timeLeft < 30000 && timeLeft > 0) {
      // Flash effect when about to expire
      const flashSpeed = 1 - (timeLeft / 30000); // Faster as it gets closer to expiry
      if (this.svgSprite) {
        this.svgSprite.alpha = 0.5 + Math.sin(now * 0.01 * (1 + flashSpeed * 5)) * 0.5;
      }
    }
  }

  // Check if a session can open this drop (ownership rules).
  canPickup(sessionId: string): boolean {
    if (!this.ownerSessionId) return true;
    if (Date.now() >= this.ownerUntil) return true;
    return this.ownerSessionId === sessionId;
  }

  // Check if player is in pickup range
  isInRange(playerX: number, playerY: number): boolean {
    const dx = playerX - this.x;
    const dy = playerY - this.baseY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= DROP_CONFIG.INTERACTION_RANGE;
  }

  // Create pickup particle effect
  createPickupEffect(): void {
    if (!this.parent) return;

    const hasEquipment = this.itemEntries.some((it) => it.itemType === 'equipment');
    const color = hasEquipment ? 0x4488ff : 0xffcc00;

    // Create expanding ring effect
    const ring = new Graphics();
    ring.position.set(this.x, this.baseY);
    ring.zIndex = 6;
    this.parent.addChild(ring);

    const maxRadius = 40;

    // Animated values object
    const animValues = { radius: 15, currentAlpha: 1 };

    tween({
      targets: animValues,
      radius: maxRadius,
      currentAlpha: 0,
      duration: 300,
      ease: 'power2.out',
      onUpdate: () => {
        ring.clear();
        ring.circle(0, 0, animValues.radius);
        ring.stroke({ width: 3, color, alpha: animValues.currentAlpha });
      },
      onComplete: () => {
        ring.destroy();
      },
    });

    // Create particle burst
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI * 2 / 6) * i;
      const particle = new Graphics();
      particle.position.set(this.x, this.baseY);
      particle.zIndex = 6;
      particle.rect(-2, -2, 4, 4); // Small square particles for crate
      particle.fill({ color, alpha: 1 });
      this.parent.addChild(particle);

      tween({
        targets: particle,
        x: this.x + Math.cos(angle) * 30,
        y: this.baseY + Math.sin(angle) * 30,
        alpha: 0,
        duration: 350,
        ease: 'power2.out',
        onComplete: () => {
          particle.destroy();
        },
      });
    }
  }

  destroy(): void {
    if (this.svgSprite) this.svgSprite.destroy();
    super.destroy();
  }
}
